import { CalendarHeart } from "lucide-react";
import { useState } from "react";
import {
  Datagrid,
  DateField,
  Edit,
  List,
  NumberField,
  NumberInput,
  SelectInput,
  SimpleForm,
  TextField,
  TextInput,
  useRecordContext,
  useUpdate,
  useNotify,
} from "react-admin";
import { Button } from "@mui/material";
import { CardListActions } from "../../components";

/** Must be spelled exactly as Hebcal spells them, as the Hebcal SDK is used
 * extensively in our different codebases. */
const HEBREW_MONTHS = [
  { id: "Tishrei", name: "Tishrei" },
  { id: "Cheshvan", name: "Cheshvan" },
  { id: "Kislev", name: "Kislev" },
  { id: "Tevet", name: "Tevet" },
  { id: "Sh'vat", name: "Sh'vat" },
  { id: "Adar", name: "Adar" },
  { id: "Adar I", name: "Adar I" },
  { id: "Adar II", name: "Adar II" },
  { id: "Nisan", name: "Nisan" },
  { id: "Iyyar", name: "Iyyar" },
  { id: "Sivan", name: "Sivan" },
  { id: "Tamuz", name: "Tamuz" },
  { id: "Av", name: "Av" },
  { id: "Elul", name: "Elul" },
];

const filters = [
  <TextInput source="name" label="Deceased's Name" alwaysOn />,
  <SelectInput
    source="hebrew_month"
    label="Hebrew Month"
    choices={HEBREW_MONTHS}
    alwaysOn
  />,
  <NumberInput source="hebrew_day" label="Hebrew Day" alwaysOn />,
  <TextInput source="submitter_email" label="Submitter Email" alwaysOn />,
];

function ListView(props) {
  return (
    <List
      {...props}
      actions={<CardListActions exporter={true} />}
      filters={filters}
      perPage={25}
      sort={{ field: "created_at", order: "DESC" }}
      title="Yahrzeits"
    >
      <Datagrid rowClick="edit">
        <TextField source="id" label="ID" />
        <TextField source="name" label="Deceased's Name" />
        <NumberField source="hebrew_day" label="Hebrew Day" />
        <TextField source="hebrew_month" label="Hebrew Month" />
        <DateField source="created_at" label="Created" showTime />
      </Datagrid>
    </List>
  );
}

function EditableSlotDate({ slot }) {
  const [update, { isPending }] = useUpdate();
  const notify = useNotify();
  const [date, setDate] = useState(slot.date_dedicated || "");
  const [isDirty, setIsDirty] = useState(false);

  const handleDateChange = (e) => {
    setDate(e.target.value);
    setIsDirty(e.target.value !== (slot.date_dedicated || ""));
  };

  const handleSave = () => {
    update(
      "dedication_slots",
      {
        id: slot.id,
        data: { date_dedicated: date },
        previousData: slot,
      },
      {
        onSuccess: () => {
          notify("Date updated successfully", { type: "success" });
          setIsDirty(false);
        },
        onError: (error) => {
          notify(`Error: ${error.message}`, { type: "error" });
        },
      },
    );
  };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <input
        type="date"
        value={date}
        onChange={handleDateChange}
        style={{
          padding: "4px 8px",
          border: "1px solid #ccc",
          borderRadius: "4px",
        }}
      />
      {isDirty && (
        <Button
          variant="contained"
          size="small"
          onClick={handleSave}
          disabled={isPending}
        >
          {isPending ? "Saving..." : "Save"}
        </Button>
      )}
    </div>
  );
}

function DedicationsList() {
  const record = useRecordContext();
  const dedications = record?.dedications || [];

  if (!dedications.length) {
    return (
      <div style={{ padding: "16px 0" }}>
        No dedications found for this yahrzeit.
      </div>
    );
  }

  return (
    <div style={{ padding: "16px 0" }}>
      <h4 style={{ margin: "0 0 16px 0" }}>Associated Dedications</h4>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ backgroundColor: "#f5f5f5" }}>
            <th
              style={{
                padding: "8px",
                textAlign: "left",
                borderBottom: "1px solid #ddd",
              }}
            >
              Submitter Name
            </th>
            <th
              style={{
                padding: "8px",
                textAlign: "left",
                borderBottom: "1px solid #ddd",
              }}
            >
              Email
            </th>
            <th
              style={{
                padding: "8px",
                textAlign: "left",
                borderBottom: "1px solid #ddd",
              }}
            >
              Submitted Date
            </th>
            <th
              style={{
                padding: "8px",
                textAlign: "left",
                borderBottom: "1px solid #ddd",
              }}
            >
              Dedication Type
            </th>
            <th
              style={{
                padding: "8px",
                textAlign: "left",
                borderBottom: "1px solid #ddd",
              }}
            >
              Date Dedicated
            </th>
          </tr>
        </thead>
        <tbody>
          {dedications.map((dedication) => {
            const submission = dedication.dedication_submission;
            const slots = dedication.dedication_slots || [];

            return slots.map((slot) => (
              <tr key={slot.id} style={{ borderBottom: "1px solid #eee" }}>
                <td style={{ padding: "8px" }}>
                  {submission?.submitter_first_name}{" "}
                  {submission?.submitter_last_name}
                </td>
                <td style={{ padding: "8px" }}>
                  {submission?.submitter_email}
                </td>
                <td style={{ padding: "8px" }}>
                  {submission?.created_at
                    ? new Date(submission.created_at).toLocaleDateString()
                    : "-"}
                </td>
                <td style={{ padding: "8px" }}>{slot.item_dedicated || "-"}</td>
                <td style={{ padding: "8px" }}>
                  <EditableSlotDate slot={slot} />
                </td>
              </tr>
            ));
          })}
        </tbody>
      </table>
    </div>
  );
}

function EditView(props) {
  return (
    <Edit {...props} mutationMode="pessimistic">
      <SimpleForm>
        <TextInput source="name" label="Deceased's Name" fullWidth />
        <NumberInput source="hebrew_day" label="Hebrew Day" />
        <SelectInput
          source="hebrew_month"
          label="Hebrew Month"
          choices={HEBREW_MONTHS}
        />
        <DedicationsList />
      </SimpleForm>
    </Edit>
  );
}

export const Yahrzeits = {
  category: "database",
  name: "yahrzeits",
  icon: CalendarHeart,
  list: ListView,
  edit: EditView,
  options: { label: "Yahrzeits" },
  roles: ["administrator"],
};
